import { Inject, Injectable, NgZone, OnDestroy } from '@angular/core';
import { NavigationEnd, Router } from '@angular/router';
import { Analytics, logEvent } from '@angular/fire/analytics';
import { environment } from 'src/environments/environment';
import { filter, Subject, takeUntil } from 'rxjs';
import { DOCUMENT } from '@angular/common';
import { LogService } from '../../helpers/log.service';

// Declaratie om TypeScript te laten weten dat globale variabelen bestaan
declare global {
  interface Window {
    dataLayer: any[];
    fbq: (...args: any[]) => void;
    [key: string]: any;
  }
}

@Injectable({
  providedIn: 'root',
})
export class TrackingService implements OnDestroy {
  // Subject om RxJS subscriptions op te schonen bij het vernietigen van de service
  private destroy$ = new Subject<void>();
  // Interne status om te weten of de Meta Pixel succesvol is geïnitialiseerd
  private isMetaPixelInitialized = false;
  // Interne status om de cookie-toestemming van de gebruiker bij te houden
  private hasConsent = false;

  constructor(
    private router: Router,
    private analytics: Analytics,
    private log: LogService,
    @Inject(DOCUMENT) private document: Document,
    private zone: NgZone // Nodig om Firebase APIs correct aan te roepen binnen Angular
  ) {}

  /**
   * Publieke methode om de service te initialiseren.
   * Deze wordt aangeroepen vanuit de AppComponent.
   */
  initialize(): void {
    // 1. Injecteer de externe scripts (GTM, Meta) op basis van de configuratie.
    this.injectScripts();

    // 2. Zorg ervoor dat de dataLayer altijd bestaat voor GTM.
    window.dataLayer = window.dataLayer || [];

    // 3. Controleer of er al toestemming is gegeven bij het laden van de app.
    const initialConsent = localStorage.getItem('cookie-accepted') === 'true';
    this.setConsentStatus(initialConsent, true); // true = is initial check

    // 4. Start met luisteren naar paginanavigaties voor het versturen van pageviews.
    this.listenForPageViews();
  }

  /**
   * Publieke methode die wordt aangeroepen vanuit de cookie-popup
   * zodra de gebruiker op "Accepteren" klikt.
   */
  public userGaveConsent(): void {
    if (this.hasConsent) {
      return; // Voorkom dubbele uitvoering als er al toestemming was.
    }
    this.setConsentStatus(true);
    this.log.info('Gebruiker heeft zojuist toestemming gewijzigd. Tracking wordt geactiveerd.');
  }

  /**
   * Publieke methode die wordt aangeroepen als een gebruiker
   * expliciet op "Afwijzen" klikt.
   */
  public userRejectedConsent(): void {
    if (!this.hasConsent) {
      return; // Geen actie nodig als er al geen consent was.
    }
    this.setConsentStatus(false);
    this.log.info('Gebruiker heeft zojuist toestemming gewijzigd. Tracking wordt gedeactiveerd.');
  }

  /**
   * Gecentraliseerde methode om de tracking status te beheren.
   * @param hasConsent Heeft de gebruiker toestemming gegeven?
   * @param isInitialCheck Is dit de eerste controle bij het laden van de app?
   */
  private setConsentStatus(hasConsent: boolean, isInitialCheck = false): void {
    this.hasConsent = hasConsent;

    // Bepaal de globale tracking status voor Google Analytics.
    window[`ga-disable-${environment.firebase.measurementId}`] = !hasConsent;

    if (!hasConsent) {
      if (isInitialCheck) {
        this.log.info('Geen cookie-toestemming gevonden. Tracking is initieel uitgeschakeld.');
      }
      return;
    }

    // Vanaf hier wordt de code alleen uitgevoerd als er consent is.
    if (isInitialCheck) {
      this.log.info('Bestaande cookie-toestemming gevonden. Tracking wordt geactiveerd.');
    } else {
      this.log.info('Gebruiker heeft zojuist toestemming gegeven. Tracking wordt geactiveerd.');
    }

    // Initialiseer de Meta Pixel nu we weten dat er consent is.
    this.initializeMetaPixel();

    // Stuur een eenmalig Firebase event om te loggen dat consent is gegeven.
    // Dit gebeurt alleen als dit niet de initiële check is (dus na een actieve klik).
    if (!isInitialCheck) {
      this.zone.run(() => {
        logEvent(this.analytics, 'cookie_consent_granted', { consent: 'granted' });
      });
    }
  }

  // Luistert continu naar navigatie-events om pageviews te versturen.
  private listenForPageViews(): void {
    this.router.events
      .pipe(
        filter((event): event is NavigationEnd => event instanceof NavigationEnd),
        takeUntil(this.destroy$)
      )
      .subscribe((event: NavigationEnd) => {
        // Gebruik de interne 'hasConsent' status als enige bron van waarheid.
        if (!this.hasConsent) {
          return;
        }

        // Stuur GTM event, alleen als een ID is geconfigureerd.
        if (environment.gtmId) {
          window.dataLayer.push({
            event: 'virtualPageview',
            page: { path: event.urlAfterRedirects },
          });
          this.log.info(`GTM virtualPageview event verstuurd voor: ${event.urlAfterRedirects}`);
        }

        // Stuur Meta Pixel event, alleen als deze succesvol is geïnitialiseerd.
        if (this.isMetaPixelInitialized) {
          window.fbq('track', 'PageView');
          this.log.info(`Meta Pixel PageView event verstuurd voor: ${event.urlAfterRedirects}`);
        }
      });
  }

  // Initialiseert de Meta Pixel eenmalig nadat toestemming is gegeven.
  private initializeMetaPixel(): void {
    if (environment.metaPixelId && typeof window.fbq === 'function' && !this.isMetaPixelInitialized) {
      window.fbq('consent', 'grant');
      window.fbq('init', environment.metaPixelId);
      this.isMetaPixelInitialized = true;
      this.log.info(`Meta Pixel geïnitialiseerd met ID: ${environment.metaPixelId}`);
    }
  }

  // Bundelt het injecteren van alle externe tracking scripts.
  private injectScripts(): void {
    if (environment.gtmId) {
      this.injectGtmScript();
    }
    if (environment.metaPixelId) {
      this.injectMetaPixelScript();
    }
  }

  // Injecteert het GTM script dynamisch in de pagina.
  private injectGtmScript(): void {
    const gtmId = environment.gtmId;
    const script = this.document.createElement('script');
    script.innerHTML = `
      (function(w,d,s,l,i){w[l]=w[l]||[];w[l].push({'gtm.start':
      new Date().getTime(),event:'gtm.js'});var f=d.getElementsByTagName(s)[0],
      j=d.createElement(s),dl=l!='dataLayer'?'&l='+l:'';j.async=true;j.src=
      'https://www.googletagmanager.com/gtm.js?id='+i+dl;f.parentNode.insertBefore(j,f);
      })(window,document,'script','dataLayer','${gtmId}');
    `;
    this.document.head.appendChild(script);

    const noscript = this.document.createElement('noscript');
    const iframe = this.document.createElement('iframe');
    iframe.src = `https://www.googletagmanager.com/ns.html?id=${gtmId}`;
    iframe.height = '0';
    iframe.width = '0';
    iframe.style.display = 'none';
    iframe.style.visibility = 'hidden';
    noscript.appendChild(iframe);
    this.document.body.prepend(noscript);
    this.log.info(`GTM script geïnjecteerd met ID: ${gtmId}`);
  }

  // Injecteert het Meta Pixel script dynamisch in de pagina.
  private injectMetaPixelScript(): void {
    const script = this.document.createElement('script');
    script.innerHTML = `
      !(function (f, b, e, v, n, t, s) {
        if (f.fbq) return;
        n = f.fbq = function () {
          n.callMethod ? n.callMethod.apply(n, arguments) : n.queue.push(arguments);
        };
        if (!f._fbq) f._fbq = n;
        n.push = n;
        n.loaded = !0;
        n.version = '2.0';
        n.queue = [];
        t = b.createElement(e);
        t.async = !0;
        t.src = v;
        s = b.getElementsByTagName(e)[0];
        s.parentNode.insertBefore(t, s);
      })(window, document, 'script', 'https://connect.facebook.net/en_US/fbevents.js');
    `;
    this.document.head.appendChild(script);
    this.log.info('Meta Pixel script geïnjecteerd.');
  }

  // Ruimt de RxJS subscription op als de service wordt vernietigd.
  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }
}
